import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { Field, FieldArrayRenderProps } from "@progress/kendo-react-form";
import {
  CustomAppDialog,
  CustomDialogCloseButton,
} from "../../components/dialog/Dialog";
import { Typography } from "@progress/kendo-react-common";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { MdDelete } from "react-icons/md";
import IconButton from "../../components/common/IconButton";

import FormSelectionField from "../../components/formFields/FormSelectionField";
import { IItem } from "../item/itemModel";
import FormNumericTextField from "../../components/formFields/FormNumericTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import { deleteBandhaiReceiptUsedByKarigarItem } from "./services/bandhaiReceipt.services";
import {
  closeBandhaiReceiptKarigarItemDeleteConfirmDialog,
  openBandhaiReceiptKarigarItemDeleteConfirmDialog,
  setBandhaiReceiptUsedByKarigarItemIndex,
} from "./bandhaiReceiptSlice";
import { findAllActiveItem } from "../item/services/item.services";
import { RATECALC } from "../../components/common/constant";

const BandhaiReceiptUsedByKarigarItemsArray = (
  fieldArrayRenderProps: FieldArrayRenderProps
) => {
  const dispatch = useAppDispatch();
  const BandhaiReceiptUsedByKarigarItemIndex = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptUsedByKarigarItemIndex
  );
  const dialogName = useAppSelector((state) => state.dialog.dialogName);
  const isViewDialog = dialogName.startsWith(
    "view-bandhai-karigar-used-items-"
  );
  const BandhaiReceiptKarigarItemDeleteConfirmDialog = useAppSelector(
    (state) => state.bandhaireceipt.BandhaiReceiptKarigarItemDeleteConfirmDialog
  );
  const fieldName = fieldArrayRenderProps.name;
  const ItemList = useAppSelector((state) => state.item.ItemList);
  const UnitList = useAppSelector((state) => state.unit.UnitList);
  const handleDeleteAction = React.useCallback(
    async (index: any) => {
      if (fieldArrayRenderProps?.value[index]?.id) {
        const response = await dispatch(
          deleteBandhaiReceiptUsedByKarigarItem(
            fieldArrayRenderProps?.value[index].id
          )
        );
        if (response.meta.requestStatus === "fulfilled") {
          fieldArrayRenderProps.onRemove({ index: index });
          dispatch(closeBandhaiReceiptKarigarItemDeleteConfirmDialog());
          dispatch(setBandhaiReceiptUsedByKarigarItemIndex(-1));
        }
      } else {
        fieldArrayRenderProps.onRemove({ index: index });
        dispatch(closeBandhaiReceiptKarigarItemDeleteConfirmDialog());
        dispatch(setBandhaiReceiptUsedByKarigarItemIndex(-1));
      }
    },
    [fieldArrayRenderProps]
  );

  const pushElementInarray = React.useCallback(() => {
    fieldArrayRenderProps?.onPush({
      value: {
        item_id: null,
        weight: null,
        qty: null,
        apply_on: null,
        unit_id: null,
        rate: null,
        total: null,
        custom_rate: null,
      },
    });
  }, [fieldArrayRenderProps]);

  const handleOpenDeleteDialog = (index: any) => {
    dispatch(openBandhaiReceiptKarigarItemDeleteConfirmDialog());
    dispatch(setBandhaiReceiptUsedByKarigarItemIndex(index));
  };

  const handleStoneWeight = (fieldArrayRenderProps: FieldArrayRenderProps) => {
    const item_used_by_kariagr =
      fieldArrayRenderProps.formRenderProps.valueGetter(fieldName);
    const itemIndex = fieldName?.split(".")?.[1];
    let stone_weight = 0;
    if (item_used_by_kariagr && item_used_by_kariagr?.length > 0) {
      item_used_by_kariagr?.forEach((karigar_item: any) => {
        stone_weight += karigar_item?.weight || 0;
      });
    }
    fieldArrayRenderProps.formRenderProps.onChange(
      `bandhai_receipt_order.${itemIndex}.stone_weight`,
      {
        value: stone_weight,
      }
    );
  };

  return (
    <>
      <CustomAppDialog open={BandhaiReceiptKarigarItemDeleteConfirmDialog}>
        <>
          <Typography.h5>{"Delete Items Used By Karigar"}</Typography.h5>
          <GridLayout>
            <GridLayoutItem>
              <Typography.p>Are you sure you want to delete this?</Typography.p>
            </GridLayoutItem>
            <GridLayoutItem
              style={{
                display: "flex",
                justifyContent: "end",
              }}
            >
              <CustomDialogCloseButton
                themeColor="error"
                closeDialogFunction={
                  closeBandhaiReceiptKarigarItemDeleteConfirmDialog
                }
              />
              <Button
                fillMode={"solid"}
                themeColor={"error"}
                onClick={() =>
                  handleDeleteAction(BandhaiReceiptUsedByKarigarItemIndex)
                }
                type="button"
              >
                Delete
              </Button>
            </GridLayoutItem>
          </GridLayout>
        </>
      </CustomAppDialog>
      <GridLayout cols={[{ width: "50%" }, { width: "50%" }]}>
        <GridLayoutItem>
          <Typography.h5 style={{ marginBottom: 5 }}>
            Items Used By Karigar
          </Typography.h5>
        </GridLayoutItem>
        <GridLayoutItem style={{ textAlign: "left", marginLeft: "auto" }}>
          {!isViewDialog && (
            <Button
              fillMode={"solid"}
              themeColor={"primary"}
              onClick={pushElementInarray}
              type="button"
            >
              Add Item
            </Button>
          )}
        </GridLayoutItem>
      </GridLayout>
      {fieldArrayRenderProps.value &&
        fieldArrayRenderProps.value.length > 0 && (
          <div
            className="table-responsive"
            style={{
              maxHeight: isViewDialog ? "50vh" : "20vh",
              scrollbarWidth: "thin",
              marginTop: 10,
            }}
          >
            <table
              className="table table-striped table-bordered table-hover field-array-table"
              style={{ minWidth: 2000 }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      width: "13%",
                    }}
                  >
                    Product Name
                  </th>
                  <th style={{ width: "8%" }}>Weight (gm)</th>
                  <th style={{ width: "7%" }}>Pcs</th>
                  <th style={{ width: "10%" }}>Rate Calculation</th>
                  <th style={{ width: "10%" }}>Unit</th>
                  <th style={{ width: "10%" }}>Rate</th>
                  <th style={{ width: "10%" }}>Total</th>
                  <th style={{ width: "10%" }}>Custom Rate</th>
                  <th style={{ width: "15%" }}>Custom Less Weight (gm)</th>
                  <th style={{ width: "3%", position: "sticky" }}></th>
                </tr>
              </thead>
              <tbody>
                {fieldArrayRenderProps.value &&
                  fieldArrayRenderProps.value.length > 0 &&
                  fieldArrayRenderProps.value.map(
                    (item_used_by_kariagr: any, index: number) => (
                      <tr>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.item_id`}
                            placeholder="Select Item"
                            isAddNew={true}
                            addNewLink="item/create"
                            dispatcherFunction={findAllActiveItem}
                            component={FormSelectionField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                            options={ItemList?.map((item: IItem) => {
                              return {
                                value: item.id,
                                label: item.item_name,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.weight`}
                            placeholder="0.000"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            onChange={() =>
                              handleStoneWeight(fieldArrayRenderProps)
                            }
                            disabled={isViewDialog}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.qty`}
                            placeholder="0.00"
                            type="number"
                            format="n2"
                            component={FormNumericTextField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                          />
                        </td>

                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.apply_on`}
                            placeholder="Select Calculation"
                            disabled={isViewDialog}
                            component={FormSelectionField}
                            validator={requiredValidator}
                            options={RATECALC?.map((rateCal: any) => {
                              return {
                                value: rateCal.value,
                                label: rateCal.label,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.unit_id`}
                            placeholder="Select Unit"
                            component={FormSelectionField}
                            validator={requiredValidator}
                            disabled={isViewDialog}
                            options={UnitList?.map((unit: any) => {
                              return {
                                value: unit.id,
                                label: unit.unit_name,
                              };
                            })}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.rate`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.total`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            disabled={true}
                            // validator={requiredValidator}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.custom_rate`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                          />
                        </td>
                        <td>
                          <Field
                            wrapperStyle={{ margin: 0 }}
                            name={`${fieldName}.${index}.customer_less_weight`}
                            placeholder="0.00"
                            type="number"
                            format="n3"
                            component={FormNumericTextField}
                            // validator={requiredValidator}
                          />
                        </td>
                        <td
                          style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 2,
                          }}
                        >
                          <IconButton
                            type="button"
                            fillMode="solid"
                            size="small"
                            title="Delete"
                            themeColor="error"
                            style={{
                              position: "absolute",
                              height: 32,
                              width: 32,
                              right: 15,
                              pointerEvents: "all",
                            }}
                            onClick={() => handleOpenDeleteDialog(index)}
                          >
                            <MdDelete
                              className="absolute-position"
                              style={{ fontSize: "22px" }}
                            />
                          </IconButton>
                        </td>
                      </tr>
                    )
                  )}
              </tbody>
            </table>
          </div>
        )}
    </>
  );
};

export default BandhaiReceiptUsedByKarigarItemsArray;
